import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNodes, useEdges } from '@xyflow/react';
import type { JSONContent } from '@tiptap/core';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import Link from '@tiptap/extension-link';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import type { TextTemplateChunk } from '@/proto/logiblocks/logiblocks';
import type { VariableSource, VariableType } from '@/proto/common';
import { getAncestorVariables, variableListItemRef } from '../variableUtils';
import { buildSuggestionItems, type SuggestionItem } from './suggestionItems';
import {
  InvalidVariableChipDecoration,
  type InvalidVariableChipDecorationStorage,
} from './InvalidVariableChipDecoration';
import {
  chunksEqual,
  editorToPlainTextChunks,
  editorToRichTextChunks,
  plainTextChunksToEditor,
  readUserMentionAttributes,
  richTextChunksToEditor,
  USER_MENTION_CHIP_SELECTOR,
  VARIABLE_CHIP_REFERENCE_ATTRIBUTES,
  VARIABLE_CHIP_SELECTOR,
  variableReferenceKey,
} from './templateSerialization';
import { InlineVariablePicker } from './InlineVariablePicker';
import { RichTextToolbar } from './RichTextToolbar';
import styles from './TextTemplateInput.module.css';
import { ReactFlowEdge, ReactFlowNode } from '@/pages/Logiblocks/types';
import {
  fetchMentionListPage,
  renderMentionListWithAPI,
  UserMentionSource,
} from './mentionList';
import { getLBNodeType } from '@/pages/Logiblocks/nodes/registry';
import useToken from '@/common/helper/useToken';

/**
 * The link mark a rich-text field offers. A link is added through the
 * toolbar's link button. Clicking one while editing places the caret, and text
 * typed at a link's end stays outside it.
 */
export const RICH_TEXT_LINK = Link.extend({
  inclusive() {
    return false;
  },
}).configure({
  openOnClick: false,
  autolink: false,
  linkOnPaste: false,
  protocols: ['http', 'https', 'mailto', 'tel'],
});

/** Attributes passed to TipTap when inserting a mention node. */
interface MentionAttrs {
  id: string;
  label: string;
  shortLabel: string;
  nodeId: string;
  variableName: string;
  derivedVariableName: string;
  source: VariableSource;
}

/** Props for the variable suggestion popup */
export interface VariablePopupState {
  items: SuggestionItem[];
  command: (item: MentionAttrs) => void;
  clientRect: DOMRect;
}

interface SuggestionPopupPosition {
  command: (item: MentionAttrs) => void;
  clientRect: DOMRect;
  /** The trigger and anything typed after it: what the picker replaces. */
  range: { from: number; to: number };
}

export interface TemplatedTextAreaProps {
  /** The ID of the current node (used to find ancestor variables) */
  nodeId: string;
  /** The field's template chunks. */
  chunks: TextTemplateChunk[];
  /**
   * Whether the field is rich text: its text chunks hold HTML, and the editor
   * offers formatting, links and a toolbar.
   */
  richText?: boolean;
  /** Shown first in a rich-text field's toolbar, such as an audience selector. */
  toolbarLeadingContent?: ReactNode;
  /** Called with the edited template */
  onChange: (chunks: TextTemplateChunk[]) => void;
  /** Whether the input is disabled/read-only */
  disabled?: boolean;
  /** Placeholder text when empty */
  placeholder?: string;
  /** true = textarea-like (multi-line), false = single-line input-like */
  multiline?: boolean;
  /** Render the editor with an input border instead of the panel treatment. */
  bordered?: boolean;
  /**
   * Size the field like the form controls around it: a single line is 36px
   * tall, like an input or select, and a multi-line field is at least 120px.
   */
  compact?: boolean;
  /** Set the text in a monospace font, for code such as a JSON body. */
  monospace?: boolean;
  /** Callback that receives a function to trigger the variable picker */
  onTriggerPickerRef?: (trigger: () => void) => void;
  /** When true, the variable picker opens as a centred modal instead of an inline popup */
  pickerAsModal?: boolean;
  /** Whether typing @ opens the user mention picker. */
  enableUserMentions?: boolean;
  /** Where the @ list gets the people an author can mention. */
  userMentionSource?: UserMentionSource;
  /** Optional response-only graph projection used by Copilot questions. */
  graphNodes?: ReactFlowNode[];
  graphEdges?: ReactFlowEdge[];
  /**
   * Character sequence that opens the variable picker while typing.
   * Defaults to a single "{". Callers whose template also has to hold literal
   * single-brace syntax (e.g. a JSON body) pass "{{" so a bare "{" no longer
   * triggers the picker. It changes only what opens the menu.
   */
  variableTriggerChar?: string;
  /**
   * Restricts the picker to these types. Left undefined (the default), a
   * meta-variable (Shipment, Alert) offers only its derived, human-facing
   * fields -- right for a message body someone will read. Passed explicitly
   * (e.g. ALL_VARIABLE_TYPES from variableUtils), a meta-variable's own
   * opaque ID is offered too, alongside its derived fields -- what a field
   * that builds a URL or a machine-readable payload actually needs.
   */
  typeFilter?: VariableType[];
  /**
   * Whether inserting a variable also adds a space after its chip. Right for
   * prose, where the next word follows. Wrong wherever the surrounding
   * characters are exact -- a JSON string, a URL, a header value -- since the
   * space would become part of the value.
   */
  chipTrailingSpace?: boolean;
  /**
   * Called when the editor loses focus. A caller that wants to normalize the
   * template on blur (e.g. pretty-printing a JSON body) does so itself by
   * updating `value` in response -- this only forwards the event.
   *
   * Held back while the variable picker is open: the picker's search box takes
   * focus, and a normalization then would rewrite the document under the
   * picker and lose the caret it is about to insert at. It is sent once the
   * picker closes, unless focus has returned to the editor by then.
   */
  onBlur?: () => void;
  /** Called when the editor gains focus. */
  onFocus?: () => void;
}

/**
 * Rich text input that renders {{nodeId.variableName}} template variables
 * as styled chips, with a variable picker popup triggered by typing `{`.
 */
export function TemplatedTextArea({
  nodeId,
  chunks,
  richText = false,
  toolbarLeadingContent,
  onChange,
  disabled = false,
  placeholder = '',
  multiline = true,
  bordered = false,
  compact = false,
  monospace = false,
  onTriggerPickerRef,
  pickerAsModal = false,
  enableUserMentions = true,
  userMentionSource = UserMentionSource.COMPANY_USERS,
  graphNodes,
  graphEdges,
  variableTriggerChar = '{',
  typeFilter,
  chipTrailingSpace = true,
  onBlur,
  onFocus,
}: TemplatedTextAreaProps) {
  const { getToken } = useToken();
  const canvasNodes = useNodes<ReactFlowNode>();
  const canvasEdges = useEdges<ReactFlowEdge>();
  const nodes = graphNodes ?? canvasNodes;
  const edges = graphEdges ?? canvasEdges;

  const ancestorVariables = useMemo(
    () => getAncestorVariables(nodeId, nodes, edges),
    [nodeId, nodes, edges]
  );

  const [popup, setPopup] = useState<SuggestionPopupPosition | null>(null);

  // The picker is open from the suggestion's start to its exit. Refs, because
  // the editor's extensions and event handlers are built once and read these
  // at event time.
  const pickerOpenRef = useRef(false);
  const blurHeldRef = useRef(false);
  const onBlurRef = useRef(onBlur);
  onBlurRef.current = onBlur;

  // The Controlling office sentinel resolves only from an alert. The @ list
  // offers it only in a workflow with an alert trigger. It's a ref because the
  // editor's extensions are built once and read it when the list opens.
  const includeControllingOfficeRef = useRef(false);
  includeControllingOfficeRef.current = nodes.some(
    (node) => getLBNodeType(node.data) === 'alert_trigger'
  );

  // The serializers for the form the field stores its chunks in.
  const chunksToEditor = richText
    ? richTextChunksToEditor
    : plainTextChunksToEditor;
  const editorToChunks = richText
    ? editorToRichTextChunks
    : editorToPlainTextChunks;

  const initialHTML = useMemo(
    () => chunksToEditor(chunks, nodes, variableTriggerChar),
    // Only compute on mount — external syncs handled separately
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure(
        richText
          ? { link: false }
          : {
              // Plain text only: formatting has nowhere to go in the stored chunks
              bold: false,
              italic: false,
              underline: false,
              strike: false,
              code: false,
              codeBlock: false,
              heading: false,
              blockquote: false,
              bulletList: false,
              orderedList: false,
              listItem: false,
              horizontalRule: false,
              link: false,
            }
      ),
      ...(richText ? [RICH_TEXT_LINK] : []),
      Placeholder.configure({ placeholder }),
      // Global class name (not CSS module) because TipTap doesn't reliably
      // apply CSS module classes to mention nodes. See TextTemplateInput.module.css.
      InvalidVariableChipDecoration.configure({
        className: 'invalidChip',
      }),

      // User mentions, typed with @. The list opens in a tippy.js popup, filled
      // from `userMentionSource` by `renderMentionListWithAPI`.
      ...(enableUserMentions
        ? [
            Mention.extend({
              name: 'userMention',
              addAttributes() {
                return {
                  id: {
                    default: null,
                    parseHTML: (element) =>
                      element.getAttribute('data-user-id'),
                    renderHTML: (attributes) => {
                      if (!attributes.id) return {};
                      return { 'data-user-id': attributes.id };
                    },
                  },
                  label: {
                    default: null,
                    parseHTML: (element) => element.getAttribute('data-label'),
                    renderHTML: (attributes) => {
                      if (!attributes.label) return {};
                      return { 'data-label': attributes.label };
                    },
                  },
                };
              },
              parseHTML() {
                return [
                  {
                    tag: USER_MENTION_CHIP_SELECTOR,
                    getAttrs: (dom) => {
                      if (typeof dom === 'string') {
                        return false;
                      }
                      const { userId, label } = readUserMentionAttributes(dom);
                      if (userId === '') {
                        return false;
                      }
                      return { id: userId, label };
                    },
                  },
                ];
              },
            }).configure({
              HTMLAttributes: {
                class: 'mention-node',
              },
              renderHTML({ node }) {
                const { id, label } = node.attrs;
                return [
                  'span',
                  {
                    class: 'mention-node',
                    'data-user-id': id,
                    'data-label': label,
                    title: `@${label}`,
                  },
                  `@${label}`,
                ];
              },
              suggestion: {
                char: '@',
                render: () => {
                  let tippyPopup: ReturnType<typeof tippy>;
                  let container: HTMLElement;
                  let updater: {
                    update: (
                      query: string,
                      newCommand?: (item: { id: string; label: string }) => void
                    ) => void;
                  } | null = null;

                  return {
                    onStart: (props) => {
                      const token = getToken();
                      if (!token) return;

                      container = document.createElement('div');
                      container.className = 'mention-dropdown';

                      updater = renderMentionListWithAPI(
                        container,
                        props.command,
                        (page, search) =>
                          fetchMentionListPage(
                            userMentionSource,
                            token,
                            page,
                            search,
                            includeControllingOfficeRef.current
                          ),
                        ''
                      );

                      tippyPopup = tippy('body', {
                        getReferenceClientRect: props.clientRect,
                        appendTo: () => document.body,
                        content: container,
                        showOnCreate: true,
                        interactive: true,
                        trigger: 'manual',
                        placement: 'bottom-start',
                        maxWidth: 'none',
                      });
                    },
                    onUpdate: (props) => {
                      const query = props.query || '';
                      updater?.update(query, props.command);
                      const instance = tippyPopup?.[0];
                      if (instance) {
                        instance.setProps({
                          getReferenceClientRect: props.clientRect,
                        });
                        if (!instance.state.isVisible) {
                          instance.show();
                        }
                      }
                    },
                    onKeyDown: (props) => {
                      if (props.event.key === 'Escape') {
                        tippyPopup?.[0]?.hide();
                        return true;
                      }
                      return false;
                    },
                    onExit: () => {
                      tippyPopup?.[0]?.destroy();
                      updater = null;
                    },
                  };
                },
              },
            }),
          ]
        : []),

      // ── {mention: variables ────────────────────────────────────────────────
      // Keep name as 'mention' so it remains backward compatible with existing
      // code, validations, and decorators in the codebase.
      // Extend Mention with a shortLabel attribute so chips can show just the
      // variable name, with the full label (in data-label) as a tooltip.
      Mention.extend({
        name: 'mention',
        parseHTML() {
          return [{ tag: VARIABLE_CHIP_SELECTOR }];
        },
        addAttributes() {
          return {
            ...this.parent?.(),
            ...VARIABLE_CHIP_REFERENCE_ATTRIBUTES,
            shortLabel: {
              default: null,
              parseHTML: (el: HTMLElement) =>
                el.getAttribute('data-short-label'),
              renderHTML: (attrs: Record<string, string>) => ({
                'data-short-label': attrs.shortLabel,
                // Tooltip showing the full label (stored in the built-in label attr)
                title: attrs.label,
              }),
            },
          };
        },
      }).configure({
        renderHTML: ({ node }) => [
          'span',
          {
            'data-type': 'mention',
            'data-id': node.attrs.id,
            'data-label': node.attrs.label,
            'data-short-label': node.attrs.shortLabel,
            'data-node-id': node.attrs.nodeId,
            'data-variable-name': node.attrs.variableName,
            'data-derived-variable-name': node.attrs.derivedVariableName,
            'data-source': String(node.attrs.source),
            title: node.attrs.label,
          },
          node.attrs.shortLabel ?? node.attrs.label ?? node.attrs.id,
        ],
        suggestion: {
          char: variableTriggerChar,
          // Allow the trigger to fire anywhere, not just after a space (default is [' '])
          allowedPrefixes: null,
          // Mention's own command always adds a space after the chip, and
          // refocuses on the next frame. Both are wrong here: a field that
          // holds exact text can't take the space, and the focus has to be
          // back in the editor by the time the picker exits (see onExit).
          command: ({ editor: ed, range, props }) => {
            const replaced = { ...range };
            const content: JSONContent[] = [
              {
                type: 'mention',
                attrs: { ...props, mentionSuggestionChar: variableTriggerChar },
              },
            ];
            if (chipTrailingSpace) {
              if (ed.state.selection.$to.nodeAfter?.text?.startsWith(' ')) {
                replaced.to += 1;
              }
              content.push({ type: 'text', text: ' ' });
            }
            ed.chain().insertContentAt(replaced, content).run();
            ed.view.focus();
          },
          render: () => ({
            onStart: (props) => {
              pickerOpenRef.current = true;
              // Fallback to editor DOM rect when clientRect is null
              // (can happen on programmatic insertion via the {+} button)
              const rect =
                props.clientRect?.() ??
                props.editor.view.dom.getBoundingClientRect();
              setPopup({
                command: props.command,
                clientRect: rect,
                range: props.range,
              });
            },
            onUpdate: (props) => {
              const rect =
                props.clientRect?.() ??
                props.editor.view.dom.getBoundingClientRect();
              setPopup({
                command: props.command,
                clientRect: rect,
                range: props.range,
              });
            },
            onExit: (props) => {
              pickerOpenRef.current = false;
              setPopup(null);
              // Send the blur held while the picker was open, unless the
              // picker's own close put the caret back in the editor.
              if (blurHeldRef.current) {
                blurHeldRef.current = false;
                if (!props.editor.isFocused) onBlurRef.current?.();
              }
            },
            onKeyDown: () => false,
          }),
        },
      }),
    ],
    content: initialHTML,
    editable: !disabled,
    editorProps: multiline
      ? {}
      : {
          handleKeyDown: (_view, event) => {
            if (event.key === 'Enter') {
              return true; // suppress newlines in single-line mode
            }
            return false;
          },
        },
    onUpdate: ({ editor: ed }) => {
      onChange(editorToChunks(ed));
    },
    onBlur: () => {
      if (pickerOpenRef.current) {
        blurHeldRef.current = true;
        return;
      }
      onBlurRef.current?.();
    },
    onFocus: () => {
      onFocus?.();
    },
  });

  // Sync editor content when the external value changes (e.g. node switching).
  useEffect(() => {
    if (!editor) {
      return;
    }
    if (!chunksEqual(editorToChunks(editor), chunks)) {
      editor.commands.setContent(
        chunksToEditor(chunks, nodes, variableTriggerChar),
        {
          emitUpdate: false,
        }
      );
    }
  }, [
    editor,
    chunks,
    nodes,
    variableTriggerChar,
    editorToChunks,
    chunksToEditor,
  ]);

  // Expose a trigger function to open the variable picker programmatically
  useEffect(() => {
    if (!editor || !onTriggerPickerRef) {
      return;
    }
    const trigger = () => {
      editor.chain().focus().insertContent(variableTriggerChar).run();
    };
    onTriggerPickerRef(trigger);
  }, [editor, onTriggerPickerRef, variableTriggerChar]);

  const containerClass = [
    styles.editorContainer,
    multiline
      ? styles.editorContainerMultiline
      : styles.editorContainerSingleLine,
    bordered ? styles.editorContainerBordered : '',
    compact ? styles.editorContainerCompact : '',
    monospace ? styles.editorContainerMonospace : '',
    disabled ? styles.editorContainerDisabled : '',
  ]
    .filter(Boolean)
    .join(' ');

  const suggestionItems: SuggestionItem[] = useMemo(
    () => buildSuggestionItems(ancestorVariables, nodes, typeFilter),
    [ancestorVariables, nodes, typeFilter]
  );

  // Keep the invalid-chip decoration in sync with available variables
  useEffect(() => {
    if (!editor) {
      return;
    }
    const validReferenceKeys = new Set(
      suggestionItems.map((item) =>
        variableReferenceKey(variableListItemRef(item.variable))
      )
    );
    const storage = editor.storage as unknown as {
      invalidVariableChipDecoration: InvalidVariableChipDecorationStorage;
    };
    storage.invalidVariableChipDecoration.validReferenceKeys =
      validReferenceKeys;
    // Dispatch empty transaction to trigger decoration recalculation
    editor.view.dispatch(editor.state.tr);
  }, [editor, suggestionItems]);

  return (
    <div className={containerClass}>
      <EditorContent editor={editor} />
      {richText && !disabled && editor && (
        <RichTextToolbar
          editor={editor}
          leadingContent={toolbarLeadingContent}
        />
      )}
      {popup && (
        <InlineVariablePicker
          state={{
            command: popup.command,
            clientRect: popup.clientRect,
            items: suggestionItems,
          }}
          asModal={pickerAsModal}
          onDeleteTrigger={() => {
            // Delete the trigger that opened the popup, then close it. The
            // range is the suggestion's own, not derived from the selection:
            // the search box has focus, so the selection is only where the
            // editor last left it.
            setPopup(null);
            if (!editor || editor.isDestroyed) {
              return;
            }
            editor.view.focus();
            const { from, to } = popup.range;
            if (
              editor.state.doc
                .textBetween(from, to)
                .startsWith(variableTriggerChar)
            ) {
              editor.chain().deleteRange({ from, to }).run();
            }
          }}
        />
      )}
    </div>
  );
}
